import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { usePreferenceScope, useStoredPreference } from '@/hooks/useStoredPreference';
import { useServerList, useDebouncedSearch } from '@/hooks/useServerList';
import { opportunityApi, opportunityParams } from '@/pages/Opportunities/api';
import { businessTimezone, dateKey, localDay, period } from '@/pages/Opportunities/dates';
import { emptyFilters, type Opportunity, type OpportunityFilters, type CalendarView } from '@/pages/Opportunities/types';

const filterSchema = z.object({ search: z.string(), status: z.string(), client_id: z.string(), responsible_id: z.string(), city: z.string(), designer: z.string(), classification: z.string(), procurement_type: z.string(), from: z.string(), to: z.string(), upcoming: z.boolean(), overdue: z.boolean(), deleted: z.boolean() });
export const useOpportunities = () => {
    const scope = usePreferenceScope();
    const [params, setParams] = useSearchParams();
    const [savedView, setSavedView] = useStoredPreference('opportunities:view', z.enum(['calendar', 'list']), 'calendar');
    const mode = params.get('view') === 'list' ? 'list' : params.get('view') === 'calendar' ? 'calendar' : savedView;
    const [calendarView, setCalendarView] = useStoredPreference<CalendarView>('opportunities:calendar', z.enum(['day', 'week', 'month', 'year']), 'month');
    const [anchor, setAnchor] = useState(() => new Date(`${localDay(new Date().toISOString())}T12:00:00`));
    const [filters, setFilters] = useStoredPreference<OpportunityFilters>('opportunities:filters', filterSchema, emptyFilters, true);
    const delayedSearch = useDebouncedSearch(filters.search);
    const effective = { ...filters, status: params.get('status') ?? filters.status, search: delayedSearch };
    const [sort, setSort] = useStoredPreference<{ key: string; order: 'asc' | 'desc' }>('opportunities:sort', z.object({ key: z.string(), order: z.enum(['asc', 'desc']) }), { key: 'submission_at', order: 'asc' });
    const range = period(anchor, calendarView);
    const from = filters.from && filters.from > dateKey(range.from) ? filters.from : dateKey(range.from);
    const to = filters.to && filters.to < dateKey(range.to) ? filters.to : dateKey(range.to);
    const calendar = useQuery({ queryKey: ['opportunities', scope, 'calendar', effective, from, to], enabled: mode === 'calendar', retry: false,
        queryFn: ({ signal }) => opportunityApi.calendar({ ...opportunityParams(effective), from, to, timezone: businessTimezone }, signal) });
    const listing = useServerList<Opportunity>('opportunities', '/opportunities', { ...opportunityParams(effective), timezone: businessTimezone, sort_by: sort.key, sort_order: sort.order });
    const catalogs = useQuery({ queryKey: ['opportunities', scope, 'catalogs'], queryFn: ({ signal }) => opportunityApi.catalogs(signal), retry: false });
    const setMode = (next: 'calendar' | 'list'): void => {
        setSavedView(next);
        const updated = new URLSearchParams(params); updated.set('view', next); setParams(updated);
    };
    const changeFilters = (next: OpportunityFilters): void => {
        setFilters(next);
        if(next.from && next.from !== filters.from){
            setAnchor(new Date(`${next.from}T12:00:00`));
        }
        if(params.has('status')){
            const updated = new URLSearchParams(params); updated.delete('status'); setParams(updated);
        }
    };
    const open = (id: number | null): void => {
        const updated = new URLSearchParams(params);
        if(id === null){ updated.delete('record'); }
        else{ updated.set('record', String(id)); }
        setParams(updated, { preventScrollReset: true });
    };
    return { mode, setMode, calendarView, setCalendarView, anchor, setAnchor, filters: { ...filters, status: effective.status }, changeFilters, sort, setSort,
        calendar, listing, catalogs, id: Number(params.get('record')) || null, open, scope };
};
